import { Badge, Select } from "@deligoo/ui"
import { ChangeEvent, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { toast } from "react-toastify"

import { Card } from "@/components/Card"
import { ConfirmModal } from "@/components/ConfirmModal"
import i18n from "@/i18n"
import { updateDriver } from "@/store/drivers"
import { Driver, PaymentForm } from "@/types"
import { DriverUtils, fetchSetDriverPaymentForms, fetchSetTemporaryDriverPaymentForms, getIconFor } from "@/utils"

import cls from "../../DriverPanel.module.scss"

const PAYMENT_FORMS: Driver["payment_forms"] = ["card", "cash"]

const PAYMENT_TURN_OFF_OPTIONS = [
  {
    key: "turn_off_day",
    disable_time: 0,
  },
  {
    key: "turn_off_two_days",
    disable_time: 1,
  },
  {
    key: "turn_off_week",
    disable_time: 6,
  },
  {
    key: "turn_off",
    disable_time: null,
  },
] as const

type PaymentTurnOffOption = typeof PAYMENT_TURN_OFF_OPTIONS[number]

export const PaymentsSection = ({ driver }: { driver: Driver }) => {
  return (
    <Card.Section className={cls.payments}>
      {PAYMENT_FORMS.map((paymentForm) => (
        <PaymentFormBadge paymentForm={paymentForm} driver={driver} key={paymentForm} />
      ))}
    </Card.Section>
  )
}

type PaymentFormBadgeProps = {
  paymentForm: NonNullable<PaymentForm>
  driver: Driver
}

const PaymentFormBadge = ({ paymentForm, driver }: PaymentFormBadgeProps) => {
  const dispatch = useDispatch()

  const isPaymentEnabled = driver.payment_forms.includes(paymentForm)

  const [turnOffOption, setTurnOffOption] = useState<PaymentTurnOffOption>(PAYMENT_TURN_OFF_OPTIONS[0])
  const [isModalShown, setIsModalShown] = useState(false)

  async function turnOnPaymentForm(paymentForm: NonNullable<PaymentForm>) {
    const paymentForms = [...driver.payment_forms, paymentForm]

    requestUpdatePaymentForms(async () => {
      const {
        response,
        json: { data: updatedDriver },
      } = await fetchSetDriverPaymentForms(driver.id, { accepted_payment_forms: paymentForms })

      if (!response.ok) throw response

      if (updatedDriver) dispatch(updateDriver(updatedDriver))
    })
  }

  async function turnOffPaymentForm(paymentForm: NonNullable<PaymentForm>) {
    requestUpdatePaymentForms(async () => {
      const {
        response,
        json: { data: updatedDriver },
      } = await fetchSetTemporaryDriverPaymentForms(driver.id, {
        payment_form: paymentForm,
        disable_time: turnOffOption.disable_time,
      })

      if (!response.ok) throw response

      if (updatedDriver) dispatch(updateDriver(updatedDriver))
    })
  }

  const { t } = useTranslation()

  return (
    <div key={paymentForm}>
      <Badge
        size="small"
        iconProps={{ name: getIconFor(paymentForm) }}
        color={DriverUtils.getPaymentColor(paymentForm, driver.payment_forms)}
        onClick={() => setIsModalShown(true)}
      >
        {t(`drivers:${paymentForm}`)}
      </Badge>

      {isModalShown && (
        <ConfirmModal
          title={t("drivers:payment_forms_toggle")}
          description={t(`drivers:payment_forms_toggle`, {
            context: isPaymentEnabled ? "off" : "on",
            paymentForm: t(`drivers:${paymentForm}`),
          })}
          contentCenter
          handleConfirm={() => {
            if (isPaymentEnabled) {
              turnOffPaymentForm(paymentForm)
            } else {
              turnOnPaymentForm(paymentForm)
            }
            setIsModalShown(false)
          }}
          handleClose={() => setIsModalShown(false)}
          autoFocusConfirm
        >
          {isPaymentEnabled && (
            <TurnOffPaymentMethodSelect turnOffOption={turnOffOption} setTurnOffOption={setTurnOffOption} />
          )}
        </ConfirmModal>
      )}
    </div>
  )
}

type TurnOffPaymentMethodSelectProps = {
  turnOffOption: PaymentTurnOffOption
  setTurnOffOption: (option: PaymentTurnOffOption) => void
}

const TurnOffPaymentMethodSelect = ({ turnOffOption, setTurnOffOption }: TurnOffPaymentMethodSelectProps) => {
  const { t } = useTranslation()

  const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const optionToSet = PAYMENT_TURN_OFF_OPTIONS.find((option) => option.key === e.currentTarget.value)
    if (optionToSet) setTurnOffOption(optionToSet)
  }

  return (
    <Select defaultValue={turnOffOption.key} onChange={handleSelectChange}>
      {PAYMENT_TURN_OFF_OPTIONS.map((option) => (
        <option key={option.key} value={option.key}>
          {t(`drivers:payment_options.${option.key}`)}
        </option>
      ))}
    </Select>
  )
}

const PAYMENT_FORMS_TOAST_SETTINGS = {
  toastId: "setDriverPaymentForms",
  updateId: "setDriverPaymentForms",
}

async function requestUpdatePaymentForms(callback: () => Promise<void>) {
  try {
    await callback()
    toast.success(i18n.t("drivers:payment_forms_update_success"), PAYMENT_FORMS_TOAST_SETTINGS)
  } catch {
    toast.error(i18n.t("drivers:payment_forms_update_fail"), PAYMENT_FORMS_TOAST_SETTINGS)
  }
}
